import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { PageContainer } from '@amzn/stencil-react-components/page';
import { Col } from "@amzn/stencil-react-components/layout";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { IconCheck, IconRefresh } from '@amzn/stencil-react-components/icons';
import { useAppDispatch, useAppSelector } from "src/state/hooks";
import { resetEventModelState, updateTrigger } from "src/state/slice/EventModelSlice";
import { DefineEventName, EventRuleComponent, SolutionComponent, TriggerSelector, BindleSelector } from "src/components/authoringComponents/index";
import { getEventModel, getTriggersSolutionTypes, upsertEventModel} from "src/service/service";
import { APICallStatus, EventModelStatus, Stage } from "src/enum/enums";
import {
  getStage,
  mandatorySolutionAttributes,
  isValidateSolutionDetailsInput,
  isValidEventRule, isNullOrUndefinedOrEmpty, isValidJson
} from "src/util/commonUtil";
import { Text } from "@amzn/stencil-react-components/text";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import {ActionType, PathNames, Regex} from "src/constant/constants";
import "src/components/commonStyles.scss";
import {TRIGGER_SOLUTION_TYPE_ERROR_MESSAGE} from "src/constant/errorConstants";
import {upsertDerivedSegmentationAttributes} from "src/util/fileUtil";
import {resetFileMetadataState} from "src/state/slice/ContactAlertFileMetadataSlice";
import {EventTimestampSelector} from "src/components/authoringComponents/EventTimestampSelector";

const STRINGS_DISPLAYED = {
  event_model_creation_error_message: "Error saving event model. Please try again.",
  save_button: "Save",
  event_model_already_exist_message: "An Event model already exists with this name. Please try again with a different name.",
  save_successful: "Successfully saved the event model!"
}

/**
 * Renders the page for creation of new event models.
 */
export const CreateEventModelPage = () => {
  const dispatch = useAppDispatch();
  const eventModelState = useAppSelector(({eventModel}) => eventModel);
  const [triggersAndSolutionTypes, setTriggersAndSolutionTypes] = useState<string[]>([]);
  const [getTriggerSolutionTypesStatus, setGetTriggerSolutionTypesStatus] = useState<APICallStatus>(APICallStatus.SUCCESSFUL);
  const contactAlertFileMetadataState = useAppSelector(({contactAlertFileMetadataComponent}) => contactAlertFileMetadataComponent);

  const [upsertEventModelStatus, setUpsertEventModelStatus] =
    useState<APICallStatus>(APICallStatus.SUCCESSFUL);
  const [errorMessage, setErrorMessage] = useState<string>(STRINGS_DISPLAYED.event_model_creation_error_message);
  const [redirectToUrl, setRedirectToUrl] = useState<string>("");
  const [requiredSolutionAttributes, setRequiredSolutionAttributes] = useState<string[]>([]);
  const [getEventModelStatus, setGetEventModelStatus] = useState<APICallStatus>(APICallStatus.NOT_STARTED);
  const [isEventModelExist, setIsEventModelExist] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isValidRule, setIsValidRule] = useState<boolean>(true)

  const onSaveClick = async () => {
    setUpsertEventModelStatus(APICallStatus.IN_PROGRESS);
    try {
      if (contactAlertFileMetadataState?.dataToBePublished.length > 0 && contactAlertFileMetadataState?.entityHeader) {
        await upsertDerivedSegmentationAttributes(eventModelState, contactAlertFileMetadataState.dataToBePublished, contactAlertFileMetadataState.entityHeader, contactAlertFileMetadataState?.existingBulkUploadFileAttributes);
      }
      await upsertEventModel(eventModelState, EventModelStatus.DRAFT, Stage.beta);
      setUpsertEventModelStatus(APICallStatus.SUCCESSFUL);
      setRedirectToUrl(`${PathNames.MANAGE_EVENT_MODELS_PAGE}/${eventModelState.eventName}`);
    } catch (error) {
      if (error?.response?.status == 403) {
        setErrorMessage(error.response.data?.message);
      }
      setUpsertEventModelStatus(APICallStatus.FAILED);
    }
  };

  const getEventModelExistingStatus = async () => {
    let status = false;
    setGetEventModelStatus(APICallStatus.IN_PROGRESS);
    // fetch draft event model from beta to check its existence
    await getEventModel(eventModelState?.eventName, EventModelStatus.DRAFT, Stage.beta)
        .then((eventModelDefinition) => {
          setGetEventModelStatus(APICallStatus.SUCCESSFUL);
          setIsEventModelExist(true);
          status = true;
        }).catch( (_) => {
          setGetEventModelStatus(APICallStatus.FAILED);
          setIsEventModelExist(false);
        });
    return status;
  };

  const validateEventName = (value: string) => {
    const pattern = new RegExp(Regex.EventNameInvalidCharRegex);
    setIsValid(!pattern.test(value));
  };

  useEffect(() => {
    setGetTriggerSolutionTypesStatus(APICallStatus.IN_PROGRESS);

    // fetch triggers and solutionTypes for the current stage
    getTriggersSolutionTypes(getStage() as Stage)
      .then(triggerAndSolutionTypes => {
        setTriggersAndSolutionTypes(triggerAndSolutionTypes);
        setGetTriggerSolutionTypesStatus(APICallStatus.SUCCESSFUL)
      }).catch( (_) => {
        setErrorMessage(TRIGGER_SOLUTION_TYPE_ERROR_MESSAGE);
      setGetTriggerSolutionTypesStatus(APICallStatus.FAILED);
    })

    // clean up state while unmounting
    dispatch(resetEventModelState());
    dispatch(resetFileMetadataState());
    }, []);

  useEffect(() => {
    setRequiredSolutionAttributes(mandatorySolutionAttributes(eventModelState?.eventSolution?.type));
  }, [eventModelState?.eventSolution?.type]);

  useEffect(() => {
    setIsEventModelExist(false);
    validateEventName(eventModelState?.eventName);
  }, [eventModelState?.eventName]);

  useEffect(() => {
    const status = false;
    getEventModelExistingStatus().then(
        (status) => {
          console.log("Status::"+ status);
          if(status == true) {
            dispatch(updateTrigger(""));
          }
        }
    );
  }, [eventModelState?.trigger]);

  useEffect(() => {
    if(isValidJson(eventModelState?.eventRule)){
      const eventRuleToValidate = eventModelState?.eventRule ? JSON.stringify(JSON.parse(eventModelState?.eventRule)) : undefined;
      setIsValidRule(isValidEventRule((eventRuleToValidate)));
    }
  }, [eventModelState?.eventRule]);

  return redirectToUrl
    ? <Redirect
      push={false}
      to={{
        pathname: redirectToUrl
      }}
    />
    : <PageContainer backgroundColor={"white"}>
      <Col gridGap={"24px"}>
        {
          (upsertEventModelStatus === APICallStatus.FAILED || getTriggerSolutionTypesStatus === APICallStatus.FAILED || isEventModelExist) &&
            <MessageBanner type={MessageBannerType.Error} autoDismissAfter={5000} onDismissed={() => setErrorMessage(STRINGS_DISPLAYED.event_model_creation_error_message)}>
              {errorMessage}
            </MessageBanner>
        }

        {
          <>
            <DefineEventName disabled={(upsertEventModelStatus === APICallStatus.IN_PROGRESS) || (!isEventModelExist && Boolean(eventModelState?.trigger))} isEventModelExist={isEventModelExist} isValid={isValid} />
            <BindleSelector disabled={false} />
            <TriggerSelector triggersAndSolutionTypes = {triggersAndSolutionTypes} />
            <EventTimestampSelector />
            <EventRuleComponent isValidRule={isValidRule}/>
            <SolutionComponent triggersAndSolutionTypes = {triggersAndSolutionTypes} actionType={ActionType.CREATE} disabled={(isEventModelExist || !eventModelState?.eventName) || !eventModelState?.trigger} />

            <Button variant={ButtonVariant.Primary}
                    icon={upsertEventModelStatus === APICallStatus.IN_PROGRESS
                        ? <IconRefresh aria-hidden={true} /> : <IconCheck aria-hidden={true} />}
                    onClick={onSaveClick}
                    disabled={!isValidateSolutionDetailsInput(eventModelState, requiredSolutionAttributes) || !isValid || !isValidRule || isEventModelExist || !(contactAlertFileMetadataState?.isValidFileFormatForBulkUpload)}
                    className={"width_fit_content"}
            >
              <Text fontSize={"16px"}>{STRINGS_DISPLAYED.save_button}</Text>
              {(upsertEventModelStatus === APICallStatus.IN_PROGRESS) && <Spinner />}
            </Button>
          </>
        }
      </Col>
    </PageContainer>
}
